import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { isApiError } from '@dorixona/api'
import { Gap } from '../../ui/Gaps'
import { withCountRu } from '@dorixona/utils'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { Icon } from '../../ui/Icon'
import { Modal } from '../../ui/Modal'
import { AutoTextarea } from '../../ui/AutoTextarea'
import { DetailHead } from '../../ui/DetailHead'
import { Failed, Loading, failureText } from '../../ui/Load'
import { useSession } from '../auth/session'
import { useProducts } from '../products/api'
import {
  useCategories,
  useDeleteCategory,
  useSaveCategory,
  useUploadCover,
  type Category,
} from './api'
import { clampCashback, inUseText, newCategory } from './model'
import { CategoryCover } from './CategoryCover'
import { CategoryProducts } from './CategoryProducts'

export interface CategoryScreenProps {
  onToast: (message: string) => void
}

type Tab = 'main' | 'prods'
type Lang = 'ru' | 'uz'

/** Всё, что во вкладке «Оформление» сохраняется одной кнопкой. */
interface Draft {
  name: string
  nameUz: string
  desc: string
  descUz: string
  /** Числа — строками: пока человек печатает, поле бывает пустым. */
  cb: string
  sort: string
}

function toDraft(category: Category): Draft {
  return {
    name: category.name,
    nameUz: category.nameUz,
    desc: category.desc,
    descUz: category.descUz,
    cb: String(category.cb),
    sort: String(category.sort),
  }
}

/** Есть ли в черновике то, чего нет на сервере. */
function changed(draft: Draft, category: Category): boolean {
  return JSON.stringify(draft) !== JSON.stringify(toDraft(category))
}

/** Какие поля черновика — тексты какого языка. */
const TEXTS: Record<Lang, { name: 'name' | 'nameUz'; desc: 'desc' | 'descUz' }> = {
  ru: { name: 'name', desc: 'desc' },
  uz: { name: 'nameUz', desc: 'descUz' },
}

const LANGS: [Lang, string][] = [
  ['ru', 'Русский'],
  ['uz', 'Oʻzbekcha'],
]

/** Сколько текстов языка заполнено — видно на переключателе, не открывая его. */
function filled(draft: Draft, lang: Lang): number {
  const { name, desc } = TEXTS[lang]
  return [draft[name], draft[desc]].filter((text) => text.trim() !== '').length
}

interface TextsProps {
  draft: Draft
  disabled: boolean
  onChange: (patch: Partial<Draft>) => void
  /** Русское название пустое — без него категорию не сохранить. */
  nameInvalid?: boolean
  onNameBlur?: () => void
}

/**
 * Название и описание по языкам. Язык — переключателем, поля — одной
 * колонкой во всю ширину; описание растёт с текстом, как у товара.
 */
function CategoryTexts({ draft, disabled, onChange, nameInvalid, onNameBlur }: TextsProps) {
  const [lang, setLang] = useState<Lang>('ru')
  const { name, desc } = TEXTS[lang]

  return (
    <section className="ui-card" aria-labelledby="c-texts">
      <div className="ui-card-head">
        <h3 id="c-texts">Название и описание</h3>
        <div className="ui-seg ui-langs" role="group" aria-label="Язык текстов">
          {LANGS.map(([value, label]) => {
            const done = filled(draft, value)
            return (
              <button
                key={value}
                type="button"
                aria-pressed={lang === value}
                onClick={() => {
                  setLang(value)
                }}
              >
                {label}
                <span
                  className="ui-count"
                  data-full={done === 2 || undefined}
                  aria-label={`заполнено ${done} из 2`}
                >
                  {done}/2
                </span>
              </button>
            )
          })}
        </div>
      </div>
      <div className="ui-card-body ui-form">
        <div className="ui-field">
          <label className="ui-label" htmlFor={`c-${lang}-name`}>
            Название
          </label>
          <input
            id={`c-${lang}-name`}
            placeholder={lang === 'uz' ? 'Masalan, Vitaminlar va BAD' : 'Например, Витамины и БАД'}
            className="ui-input"
            lang={lang}
            disabled={disabled}
            aria-invalid={lang === 'ru' && nameInvalid ? true : undefined}
            value={draft[name]}
            onChange={(e) => {
              onChange({ [name]: e.target.value })
            }}
            onBlur={lang === 'ru' ? onNameBlur : undefined}
          />
        </div>
        <div className="ui-field">
          <label className="ui-label" htmlFor={`c-${lang}-desc`}>
            Описание
          </label>
          <AutoTextarea
            id={`c-${lang}-desc`}
            placeholder={
              lang === 'uz'
                ? 'Toifada nima borligi haqida bir-ikki gap'
                : 'Пара фраз о том, что в категории'
            }
            lang={lang}
            disabled={disabled}
            value={draft[desc]}
            onChange={(e) => {
              onChange({ [desc]: e.target.value })
            }}
          />
        </div>
      </div>
    </section>
  )
}

/**
 * Карточка категории: как она выглядит на витрине и что в неё входит.
 * Состав собирается из товаров — в учётной системе категории не заводятся.
 *
 * Правки вкладки «Оформление» копятся в черновике и уходят одной кнопкой
 * «Сохранить». Черновик хранит только правки: категория с сервера меняется
 * и без них (переключатель видимости, новая обложка), и эти ответы не должны
 * стирать то, что человек успел напечатать.
 *
 * Новая категория (`/categories/new`) — та же форма без состава и показателей:
 * создаётся целиком одной кнопкой, скрытой; обложка уходит сразу следом.
 */
export function CategoryScreen({ onToast }: CategoryScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const params = useParams()
  const [tab, setTab] = useState<Tab>('main')
  const creating = params.id === undefined
  const id = creating ? 0 : Number(params.id)

  const categories = useCategories()
  const saveOne = useSaveCategory()
  const remove = useDeleteCategory()
  const upload = useUploadCover()
  const [deleting, setDeleting] = useState(false)
  // Обложка новой категории: файл ждёт создания категории.
  const [coverFile, setCoverFile] = useState<File | null>(null)
  // Пустое название краснеет, когда из поля ушли: новая категория
  // открывается пустой, и форма в красном выглядела бы ошибкой.
  const [nameLeft, setNameLeft] = useState(false)
  // Показатели — итогами сервера: по странице товаров их не сосчитать.
  // Страница не нужна, только `total`. «Товаров» — все, и скрытые тоже:
  // столько же строк во вкладке, и столько мешает удалить категорию.
  // У новой категории товаров нет — не спрашиваем.
  const all = useProducts({ categoryId: id, pageSize: 1 }, !creating)
  const shown = useProducts({ categoryId: id, active: true, pageSize: 1 }, !creating)
  const rx = useProducts({ categoryId: id, rx: 'rx', pageSize: 1 }, !creating)

  const category = creating
    ? newCategory('', categories.data ?? [])
    : categories.data?.find((c) => c.id === id)
  // null — правок нет, форма показывает то, что на сервере.
  const [edits, setEdits] = useState<Draft | null>(null)

  // Другая категория — другой черновик.
  useEffect(() => {
    setEdits(null)
  }, [id])

  // Категорию могли удалить, пока здесь правили, — тогда и сохранять некуда.
  const dirty =
    (edits !== null && category !== undefined && changed(edits, category)) || coverFile !== null

  // Уход со страницы с несохранёнными правками браузер переспросит.
  useUnsavedWarning(dirty)

  if (categories.isPending) return <Loading what="категорию" />
  if (categories.isError) {
    return (
      <Failed
        what="категорию"
        error={categories.error}
        onRetry={() => {
          void categories.refetch()
        }}
      />
    )
  }

  if (creating && !can('categories', 'c')) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-tag" />
          <b>Создавать категории роль не разрешает</b>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/categories')
            }}
          >
            К списку категорий
          </button>
        </div>
      </div>
    )
  }

  if (!category) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-tag" />
          <b>Категория не найдена</b>
          <p>Возможно, её удалили.</p>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/categories')
            }}
          >
            К списку категорий
          </button>
        </div>
      </div>
    )
  }

  const current: Category = category
  const draft = edits ?? toDraft(current)
  const edit = (patch: Partial<Draft>) => {
    setEdits({ ...draft, ...patch })
  }

  const canUpdate = can('categories', creating ? 'c' : 'u')
  const count = all.data?.total ?? category.products
  const nameMissing = draft.name.trim() === ''
  // Правка краснеет сразу, как название стёрли; новая — когда из пустого поля ушли.
  const nameInvalid = nameMissing && (!creating || nameLeft)
  const busy = saveOne.isPending || upload.isPending

  /**
   * Удаление. Пустую категорию сервер удаляет, с товарами — отвечает 409
   * `in_use`: тогда говорим, что делать, а не «запрос не прошёл».
   */
  function removeCategory() {
    remove.mutate(current.id, {
      onSuccess: () => {
        onToast('Категория удалена')
        void navigate('/categories')
      },
      onError: (error) => {
        setDeleting(false)
        onToast(
          isApiError(error) && error.code === 'in_use'
            ? inUseText(all.data?.total)
            : failureText(error),
        )
      },
    })
  }

  function toggleActive(active: boolean) {
    saveOne.mutate(
      { ...current, active },
      {
        onSuccess: () => {
          onToast(active ? 'Категория показана в приложении' : 'Категория скрыта')
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  function saveAll() {
    saveOne.mutate(
      {
        ...current,
        name: draft.name,
        nameUz: draft.nameUz,
        desc: draft.desc,
        descUz: draft.descUz,
        cb: clampCashback(Number(draft.cb)),
        sort: Number(draft.sort),
      },
      {
        onSuccess: () => {
          setEdits(null)
          onToast('Изменения сохранены — обновятся в приложении')
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  /** Кэшбэк и порядок — одинаковы у новой категории и у правки. */
  const showcase = (
    <section className="ui-card" aria-labelledby="c-showcase">
      <div className="ui-card-head">
        <h3 id="c-showcase">Витрина</h3>
      </div>
      <div className="ui-card-body ui-form">
        <div className="ui-field">
          <label className="ui-label" htmlFor="c-cb">
            Кэшбэк по категории
          </label>
          <div className="ui-unit">
            <input
              id="c-cb"
              className="ui-input"
              type="number"
              inputMode="numeric"
              min={0}
              max={30}
              disabled={!canUpdate}
              aria-describedby="c-cb-hint"
              value={draft.cb}
              onChange={(e) => {
                edit({ cb: e.target.value })
              }}
            />
            <span aria-hidden="true">%</span>
          </div>
          <span className="ui-hint" id="c-cb-hint">
            Для товаров без своего процента · 0–30
          </span>
        </div>
        <div className="ui-field">
          <label className="ui-label" htmlFor="c-sort">
            Порядок показа
          </label>
          <input
            id="c-sort"
            className="ui-input"
            type="number"
            inputMode="numeric"
            min={1}
            disabled={!canUpdate}
            value={draft.sort}
            onChange={(e) => {
              edit({ sort: e.target.value })
            }}
          />
        </div>
      </div>
    </section>
  )

  const toCategory = (): Category => ({
    ...current,
    name: draft.name.trim(),
    nameUz: draft.nameUz,
    desc: draft.desc,
    descUz: draft.descUz,
    cb: clampCashback(Number(draft.cb)),
    sort: Number(draft.sort) || current.sort,
  })

  /** Новая категория — целиком одним запросом, скрытой; обложка — сразу следом. */
  async function createCategory() {
    let saved: Category
    try {
      saved = await saveOne.mutateAsync(toCategory())
    } catch (error) {
      onToast(failureText(error))
      return
    }
    let coverFailed = false
    if (coverFile) {
      try {
        await upload.mutateAsync({ id: saved.id, file: coverFile })
      } catch {
        coverFailed = true
      }
    }
    setEdits(null)
    setCoverFile(null)
    onToast(
      coverFailed
        ? 'Категория создана, но обложка не загрузилась — добавьте её ещё раз'
        : 'Категория создана — покажите её в приложении, когда будет готова',
    )
    void navigate(`/categories/${saved.id}`)
  }

  if (creating) {
    return (
      <>
        <DetailHead
          backLabel="Категории"
          onBack={() => {
            void navigate('/categories')
          }}
          title="Новая категория"
          sub={null}
        />

        <div className="ui-detail-grid">
          <div className="ui-detail-col">
            <CategoryTexts
              draft={draft}
              disabled={false}
              onChange={edit}
              nameInvalid={nameInvalid}
              onNameBlur={() => {
                setNameLeft(true)
              }}
            />
          </div>
          <div className="ui-detail-col">
            {showcase}
            <CategoryCover
              category={current}
              canUpdate
              onToast={onToast}
              local={{ file: coverFile, onChange: setCoverFile }}
            />
          </div>
        </div>

        <div className="ui-savebar" data-dirty={dirty || undefined}>
          <span className="state" role="status">
            {nameMissing ? 'Укажите название на русском' : 'Категория ещё не создана'}
          </span>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            disabled={busy}
            onClick={() => {
              void navigate('/categories')
            }}
          >
            {dirty ? 'Отменить' : 'К списку'}
          </button>
          <button
            type="button"
            className="ui-btn"
            disabled={nameMissing || busy}
            aria-busy={busy}
            onClick={() => {
              void createCategory()
            }}
          >
            Создать категорию
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <DetailHead
        backLabel="Категории"
        onBack={() => {
          void navigate('/categories')
        }}
        title={category.name}
        sub={
          <>
            <span>
              кэшбэк <b className="cat-cb">{category.cb}%</b>
            </span>
            <span className="dot" aria-hidden="true" />
            <span>{withCountRu(count, ['товар', 'товара', 'товаров'])}</span>
            <span className="dot" aria-hidden="true" />
            <span>порядок {category.sort}</span>
          </>
        }
        actions={
          <>
            {canUpdate ? (
              <label className="ui-switch cat-active">
                <input
                  type="checkbox"
                  role="switch"
                  checked={category.active}
                  onChange={(e) => {
                    toggleActive(e.target.checked)
                  }}
                />
                Показывать в приложении
              </label>
            ) : (
              <>
                <span className="ui-status" data-tone={category.active ? 'success' : 'neutral'}>
                  {category.active ? 'в приложении' : 'скрыта'}
                </span>
                <span className="ui-tag">только просмотр</span>
              </>
            )}
            {/* Удаление — редкое действие: тихая иконка с подсказкой, а не
                красная кнопка рядом с главным. Подтверждается окном. */}
            {can('categories', 'd') ? (
              <button
                type="button"
                className="ui-iconbtn ui-danger-icon"
                aria-label="Удалить категорию"
                data-tip="Удалить категорию"
                data-tip-pos="bottom"
                data-tip-align="end"
                onClick={() => {
                  setDeleting(true)
                }}
              >
                <Icon name="i-trash" />
              </button>
            ) : null}
          </>
        }
      />

      <div className="ui-tabs ui-detail-tabs" role="tablist" aria-label="Разделы карточки">
        <button
          type="button"
          role="tab"
          className="ui-tab"
          aria-selected={tab === 'main'}
          onClick={() => {
            setTab('main')
          }}
        >
          Оформление
          {/* Правки не пропадают при переходе на другую вкладку — точка
              напоминает, что они ждут сохранения. */}
          {dirty ? (
            <span className="ui-dirty-dot" aria-label="есть несохранённые изменения" />
          ) : null}
        </button>
        <button
          type="button"
          role="tab"
          className="ui-tab"
          aria-selected={tab === 'prods'}
          onClick={() => {
            setTab('prods')
          }}
        >
          Товары
          <span className="ui-count">{count}</span>
        </button>
      </div>

      {tab === 'main' ? (
        <>
          <div className="ui-detail-grid">
            <div className="ui-detail-col">
              <CategoryTexts
                draft={draft}
                disabled={!canUpdate}
                onChange={edit}
                nameInvalid={nameInvalid}
              />

              <section className="ui-card" aria-labelledby="c-stats">
                <div className="ui-card-head">
                  <h3 id="c-stats">Показатели</h3>
                </div>
                <div className="ui-card-body">
                  {/* Плитки, а не строки: числа читаются с одного взгляда.
                      Пока итог не пришёл — прочерк, а не ноль. */}
                  <dl className="ui-minis">
                    <div>
                      <dt>Товаров</dt>
                      <dd>{count}</dd>
                    </div>
                    <div>
                      <dt>В приложении</dt>
                      <dd>{shown.data?.total ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>По рецепту</dt>
                      <dd>{rx.data?.total ?? '—'}</dd>
                    </div>
                    {/* Числа нет — на его месте метка «нет в API»: плитка той же
                        высоты, что и остальные, ряд не раздувается. */}
                    <div data-gap>
                      <dt>Выручка</dt>
                      <dd>
                        <Gap what="Выручка категории" where="GET /admin/categories/{id}" />
                      </dd>
                    </div>
                  </dl>
                </div>
              </section>
            </div>

            <div className="ui-detail-col">
              {showcase}

              <CategoryCover category={category} canUpdate={canUpdate} onToast={onToast} />
            </div>
          </div>

          {canUpdate ? (
            <div className="ui-savebar" data-dirty={dirty || undefined}>
              <span className="state" role="status">
                {dirty && nameMissing
                  ? 'Укажите название на русском'
                  : dirty
                    ? 'Есть несохранённые изменения'
                    : 'Все изменения сохранены'}
              </span>
              {dirty ? (
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="secondary"
                  disabled={saveOne.isPending}
                  onClick={() => {
                    setEdits(null)
                  }}
                >
                  Отменить
                </button>
              ) : null}
              <button
                type="button"
                className="ui-btn"
                disabled={!dirty || nameMissing || saveOne.isPending}
                aria-busy={saveOne.isPending}
                onClick={saveAll}
              >
                Сохранить
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <CategoryProducts key={category.id} category={category} />
      )}

      {deleting ? (
        <Modal
          title="Удалить категорию?"
          onClose={() => {
            setDeleting(false)
          }}
        >
          <p>
            «{category.name}» пропадёт из админки и из приложения. Вернуть её можно только созданием
            заново.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setDeleting(false)
              }}
            >
              Не удалять
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={remove.isPending}
              aria-busy={remove.isPending}
              onClick={removeCategory}
            >
              Удалить
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}
